import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, CreditCard, QrCode, LogOut,
  BarChart3, FileText, Mail, BookOpen, Shield,
  Save, Loader2, Eye, Settings, Layout as LayoutIcon,
  ListChecks, AlertCircle,
} from "lucide-react";
import { FieldRow, ArrayEditor, IconPicker, ColorPicker } from "../components/admin/CMSFieldEditors";
import { invalidateCMS } from "../hooks/useCMSContent";

const API = "/api";

const findPlaceholders = (obj, prefix = "") => {
  const out = [];
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    for (const k of Object.keys(obj)) {
      const p = prefix ? `${prefix}.${k}` : k;
      out.push(...findPlaceholders(obj[k], p));
    }
  } else if (Array.isArray(obj)) {
    obj.forEach((v, i) => out.push(...findPlaceholders(v, `${prefix}[${i}]`)));
  } else if (typeof obj === "string" && obj.includes("[COMPLETAR POR ADMIN]")) {
    out.push(prefix);
  }
  return out;
};

const AdminSiteContent = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("landing");
  const [landingTab, setLandingTab] = useState("hero");

  const [content, setContent] = useState({
    terms_conditions: "",
    about_us: "",
    contact_us: "",
    complaints_book: "",
    privacy_policy: "",
    landing_json: null,
    contact_json: null,
    usos_json: null,
    about_json: null,
  });

  useEffect(() => { fetchContent(); fetchI18nStatus(); }, []);

  // iter68 F5 — freshness indicator per language (ES = single source of truth)
  const [i18nStatus, setI18nStatus] = useState(null);
  const [resyncing, setResyncing] = useState(false);

  const fetchI18nStatus = async () => {
    try {
      const token = localStorage.getItem("avisocar_token");
      const r = await axios.get(`${API}/admin/site-content/i18n-status`, { headers: { Authorization: `Bearer ${token}` } });
      setI18nStatus(r.data.sections);
    } catch (_e) { /* panel opcional */ }
  };

  const resyncEN = async (sections = null) => {
    setResyncing(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      const r = await axios.post(`${API}/admin/site-content/resync`, sections ? { sections } : {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`EN re-sincronizado (${r.data.total_fields} campos traducidos, pendientes de tu revisión)`);
      await fetchI18nStatus();
    } catch (_e) {
      toast.error("Error al re-sincronizar EN");
    } finally { setResyncing(false); }
  };

  const approveEN = async (section) => {
    try {
      const token = localStorage.getItem("avisocar_token");
      await axios.post(`${API}/admin/site-content/approve-en`, { section },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Traducción EN aprobada ✅");
      await fetchI18nStatus();
    } catch (_e) { toast.error("Error al aprobar"); }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem("avisocar_token");
      const r = await axios.get(`${API}/admin/site-content`, { headers: { Authorization: `Bearer ${token}` } });
      setContent({
        terms_conditions: r.data.terms_conditions || "",
        about_us: r.data.about_us || "",
        contact_us: r.data.contact_us || "",
        complaints_book: r.data.complaints_book || "",
        privacy_policy: r.data.privacy_policy || "",
        landing_json: r.data.landing_json,
        contact_json: r.data.contact_json,
        usos_json: r.data.usos_json,
        about_json: r.data.about_json,
      });
    } catch (_e) {
      toast.error("Error al cargar contenido");
    } finally { setLoading(false); }
  };

  const saveSection = async (payload, label = "sección") => {
    setSaving(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      const r = await axios.put(`${API}/admin/site-content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      invalidateCMS();
      toast.success(`Guardado — la ${label} se actualizará en el sitio en menos de 60s`);
      // iter68 F5 — an ES save auto-queues the EN re-translation
      if ((r.data?.i18n_resync_queued || []).length > 0) {
        toast.info("EN se está re-traduciendo automáticamente (quedará pendiente de tu revisión)");
        setTimeout(fetchI18nStatus, 8000);
      }
    } catch (_e) {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const setLanding = (patch) => setContent((c) => ({ ...c, landing_json: { ...c.landing_json, ...patch } }));
  const setLandingSection = (section, patch) => setLanding({ [section]: { ...content.landing_json?.[section], ...patch } });

  const pending = useMemo(() => {
    const out = [];
    out.push(...findPlaceholders(content.landing_json, "landing_json"));
    out.push(...findPlaceholders(content.contact_json, "contact_json"));
    out.push(...findPlaceholders(content.usos_json, "usos_json"));
    for (const k of ["terms_conditions","about_us","contact_us","complaints_book","privacy_policy"]) {
      if (typeof content[k] === "string" && content[k].includes("[COMPLETAR POR ADMIN]")) out.push(k);
    }
    return out;
  }, [content]);

  const legalTabs = [
    { id: "terms", label: "Términos", field: "terms_conditions", icon: FileText, previewUrl: "/terminos", fullLabel: "Términos y Condiciones" },
    { id: "privacy", label: "Privacidad", field: "privacy_policy", icon: Shield, previewUrl: "/privacidad", fullLabel: "Política de Privacidad" },
    { id: "about", label: "Nosotros (doc legal)", field: "about_us", icon: Users, previewUrl: "/sobre-nosotros", fullLabel: "Sobre Nosotros (doc legal del footer)" },
    { id: "contact", label: "Contacto (Markdown)", field: "contact_us", icon: Mail, previewUrl: "/contacto", fullLabel: "Contáctenos (Markdown legado)" },
    { id: "complaints", label: "Reclamos", field: "complaints_book", icon: BookOpen, previewUrl: "/reclamaciones", fullLabel: "Libro de Reclamaciones" },
  ];

  const l = content.landing_json || {};

  return (
    <div className="min-h-screen bg-[#0B2545]">


      <main className="p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Contenido del Sitio Web</h1>
              <p className="text-slate-400 mt-1">Edita cada bloque del landing y las páginas informativas</p>
            </div>
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="cms-pending-counter">
              <ListChecks className="w-5 h-5 text-amber-400" />
              <div className="text-sm">
                <span className="text-amber-200 font-semibold">{pending.length}</span>
                <span className="text-amber-100/80"> pendiente(s) [COMPLETAR POR ADMIN]</span>
              </div>
            </div>
          </div>

          {pending.length > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/40 mb-4" data-testid="cms-pending-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-300 mb-2 font-semibold">
                  <AlertCircle className="w-4 h-4" /> Checklist de pendientes
                </div>
                <ul className="text-xs text-amber-100/90 space-y-1 max-h-40 overflow-y-auto">
                  {pending.map((p, i) => <li key={i} className="font-mono">• {p}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* iter68 F5 — estado de idiomas (ES = fuente única de verdad) */}
          {i18nStatus && (
            <Card className="bg-[#14375F]/60 border-white/15 mb-4" data-testid="cms-i18n-panel">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="text-sm font-bold text-white">Idiomas — EN se traduce automáticamente desde el ES vigente</p>
                  <Button size="sm" onClick={() => resyncEN()} disabled={resyncing}
                    className="bg-sky-600 hover:bg-sky-500 h-8 text-xs" data-testid="cms-i18n-resync-btn">
                    {resyncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Re-sincronizar EN ahora
                  </Button>
                </div>
                <div className="flex gap-2 flex-wrap text-xs">
                  {Object.entries({ landing_json: "Landing", contact_json: "Contacto", usos_json: "Usos", about_json: "Nosotros" }).map(([k, label]) => {
                    const st = i18nStatus[k]?.status || "stale";
                    const icon = st === "synced" ? "✅" : st === "auto" ? "🔄" : "⚠️";
                    const hint = st === "synced" ? "sincronizado (revisado)" : st === "auto" ? "auto-traducido, pendiente de revisión" : "desfasado";
                    return (
                      <span key={k} className="inline-flex items-center gap-1.5 bg-[#0B2545] border border-white/15 rounded-full px-3 py-1.5" data-testid={`cms-i18n-${k}`} title={hint}>
                        <span className="text-slate-300">{label} · EN</span> {icon}
                        {st === "auto" && (
                          <button onClick={() => approveEN(k)} className="text-[#5BC0FF] underline ml-1" data-testid={`cms-i18n-approve-${k}`}>aprobar</button>
                        )}
                      </span>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-2">
                  EN no es editable como fuente independiente: cada edición ES lo re-traduce automáticamente. PT sirve ES hasta su ronda (el mecanismo ya lo cubre).
                </p>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#5BC0FF] animate-spin" /></div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#14375F] mb-4 flex-wrap h-auto">
                <TabsTrigger value="landing" className="data-[state=active]:bg-[#1B84FF]" data-testid="cms-tab-landing"><LayoutIcon className="w-4 h-4 mr-2" />Landing</TabsTrigger>
                <TabsTrigger value="about" className="data-[state=active]:bg-[#1B84FF]" data-testid="cms-tab-about"><Users className="w-4 h-4 mr-2" />Página Nosotros</TabsTrigger>
                <TabsTrigger value="contact" className="data-[state=active]:bg-[#1B84FF]" data-testid="cms-tab-contact"><Mail className="w-4 h-4 mr-2" />Contacto</TabsTrigger>
                <TabsTrigger value="legal" className="data-[state=active]:bg-[#1B84FF]" data-testid="cms-tab-legal"><FileText className="w-4 h-4 mr-2" />Docs legales</TabsTrigger>
              </TabsList>

              {/* ============ LANDING ============ */}
              <TabsContent value="landing">
                <Card className="bg-[#14375F] border-white/15">
                  <CardContent className="p-4 md:p-6">
                    <Tabs value={landingTab} onValueChange={setLandingTab}>
                      <TabsList className="bg-slate-700 mb-4 flex-wrap h-auto">
                        {["hero","how","real","pricing","faq","cta","footer","usos"].map((k) => (
                          <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#1B84FF]" data-testid={`cms-landing-tab-${k}`}>{k.toUpperCase()}</TabsTrigger>
                        ))}
                      </TabsList>

                      {/* HERO */}
                      <TabsContent value="hero" className="space-y-3">
                        <FieldRow label="Badge comercial (el precio lo inyecta el plan publicado)" value={l.hero?.badge} onChange={(v) => setLandingSection("hero", { badge: v })} testid="cms-hero-badge" />
                        <FieldRow label="Título principal (H1)" value={l.hero?.title} onChange={(v) => setLandingSection("hero", { title: v })} testid="cms-hero-title" />
                        <FieldRow label="Subtítulo" value={l.hero?.subtitle} onChange={(v) => setLandingSection("hero", { subtitle: v })} testid="cms-hero-subtitle" multiline />
                        <div className="grid grid-cols-3 gap-3">
                          <FieldRow label="Badge 1" value={l.hero?.bullet_1} onChange={(v) => setLandingSection("hero", { bullet_1: v })} testid="cms-hero-b1" />
                          <FieldRow label="Badge 2" value={l.hero?.bullet_2} onChange={(v) => setLandingSection("hero", { bullet_2: v })} testid="cms-hero-b2" />
                          <FieldRow label="Badge 3" value={l.hero?.bullet_3} onChange={(v) => setLandingSection("hero", { bullet_3: v })} testid="cms-hero-b3" />
                        </div>
                        <FieldRow label="CTA principal" value={l.hero?.cta_primary} onChange={(v) => setLandingSection("hero", { cta_primary: v })} testid="cms-hero-cta" />
                        <FieldRow label="Nota bajo CTA" value={l.hero?.cta_note} onChange={(v) => setLandingSection("hero", { cta_note: v })} testid="cms-hero-cta-note" />
                        <Button onClick={() => saveSection({ landing_json: l }, "hero")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-hero-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar Hero
                        </Button>
                      </TabsContent>

                      {/* HOW */}
                      <TabsContent value="how" className="space-y-3">
                        <FieldRow label="Título sección" value={l.how_it_works?.title} onChange={(v) => setLandingSection("how_it_works", { title: v })} testid="cms-how-title" />
                        <FieldRow label="Subtítulo" value={l.how_it_works?.subtitle} onChange={(v) => setLandingSection("how_it_works", { subtitle: v })} testid="cms-how-subtitle" />
                        {[1,2,3].map((n) => (
                          <div key={n} className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#0B2545]/40 p-3 rounded">
                            <FieldRow label={`Paso ${n} — Título`} value={l.how_it_works?.[`step_${n}_title`]} onChange={(v) => setLandingSection("how_it_works", { [`step_${n}_title`]: v })} testid={`cms-how-s${n}-title`} />
                            <FieldRow label={`Paso ${n} — Texto`} value={l.how_it_works?.[`step_${n}_text`]} onChange={(v) => setLandingSection("how_it_works", { [`step_${n}_text`]: v })} testid={`cms-how-s${n}-text`} multiline />
                          </div>
                        ))}
                        <Button onClick={() => saveSection({ landing_json: l }, "sección Cómo funciona")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-how-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                        </Button>
                      </TabsContent>

                      {/* REAL LIFE CASES */}
                      <TabsContent value="real" className="space-y-3">
                        <FieldRow label="Título" value={l.real_life?.title} onChange={(v) => setLandingSection("real_life", { title: v })} testid="cms-real-title" />
                        <FieldRow label="Subtítulo" value={l.real_life?.subtitle} onChange={(v) => setLandingSection("real_life", { subtitle: v })} testid="cms-real-subtitle" />
                        <ArrayEditor
                          items={l.real_life?.cases || []}
                          onChange={(next) => setLandingSection("real_life", { cases: next })}
                          blankItem={{ icon: "Car", title: "Nuevo caso", color: "from-emerald-500 to-emerald-600" }}
                          testidPrefix="cms-real-case"
                          itemLabel="Caso"
                          renderItem={(item, patch) => (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <FieldRow label="Título del caso" value={item.title} onChange={(v) => patch({ title: v })} testid="cms-real-case-title" />
                              <IconPicker value={item.icon} onChange={(v) => patch({ icon: v })} testid="cms-real-case-icon" />
                              <div className="md:col-span-2">
                                <ColorPicker value={item.color} onChange={(v) => patch({ color: v })} testid="cms-real-case-color" />
                              </div>
                            </div>
                          )}
                        />
                        <Button onClick={() => saveSection({ landing_json: l }, "casos de la vida real")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-real-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar casos
                        </Button>
                      </TabsContent>

                      {/* PRICING */}
                      <TabsContent value="pricing" className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <FieldRow label="Título tarjeta" value={l.pricing?.card_title} onChange={(v) => setLandingSection("pricing", { card_title: v })} testid="cms-price-title" />
                          <FieldRow label="Moneda (S/, $, ARS)" value={l.pricing?.amount_currency} onChange={(v) => setLandingSection("pricing", { amount_currency: v })} testid="cms-price-currency" />
                          <FieldRow label="Precio (50)" value={l.pricing?.amount} onChange={(v) => setLandingSection("pricing", { amount: v })} testid="cms-price-amount" />
                        </div>
                        <FieldRow label="Período (lo define el plan publicado)" value={l.pricing?.period_label} onChange={(v) => setLandingSection("pricing", { period_label: v })} testid="cms-price-period" />
                        <FieldRow label="Nota internacional" value={l.pricing?.intl_note} onChange={(v) => setLandingSection("pricing", { intl_note: v })} testid="cms-price-intl-note" multiline />
                        <FieldRow label="CTA botón" value={l.pricing?.cta} onChange={(v) => setLandingSection("pricing", { cta: v })} testid="cms-price-cta" />
                        <FieldRow label="Nota bajo CTA" value={l.pricing?.cta_note} onChange={(v) => setLandingSection("pricing", { cta_note: v })} testid="cms-price-cta-note" />

                        <div className="pt-3 border-t border-white/15">
                          <p className="text-slate-300 font-semibold mb-2">Features de la tarjeta</p>
                          <ArrayEditor
                            items={l.pricing?.features || []}
                            onChange={(next) => setLandingSection("pricing", { features: next })}
                            blankItem="Nueva feature"
                            testidPrefix="cms-price-feat"
                            itemLabel="Feature"
                            renderItem={(item, _patch) => (
                              <FieldRow
                                label="Feature"
                                value={item}
                                onChange={(v) => {
                                  // ArrayEditor's `patch` expects a partial object, but here items are plain strings.
                                  // Replace by rebuilding around index — the ArrayEditor.update passes { ...it, ...patch }
                                  // which won't work for strings; we thus special-case with a small workaround:
                                  const arr = [...(l.pricing?.features || [])];
                                  const idx = arr.indexOf(item);
                                  if (idx >= 0) { arr[idx] = v; setLandingSection("pricing", { features: arr }); }
                                }}
                                testid="cms-price-feat-label"
                              />
                            )}
                          />
                        </div>

                        <div className="pt-3 border-t border-white/15">
                          <p className="text-slate-300 font-semibold mb-2">Métodos de pago — Perú</p>
                          <FieldRow label="Etiqueta grupo Perú" value={l.pricing?.payment_methods_pe_label} onChange={(v) => setLandingSection("pricing", { payment_methods_pe_label: v })} testid="cms-price-pe-label" />
                          <ArrayEditor
                            items={l.pricing?.payment_methods_pe || []}
                            onChange={(next) => setLandingSection("pricing", { payment_methods_pe: next })}
                            blankItem={{ icon: "Card", label: "Nuevo método" }}
                            testidPrefix="cms-price-method-pe"
                            itemLabel="Método"
                            renderItem={(item, patch) => (
                              <div className="grid grid-cols-2 gap-3">
                                <FieldRow label="Etiqueta" value={item.label} onChange={(v) => patch({ label: v })} testid="cms-price-method-pe-label" />
                                <IconPicker value={item.icon} onChange={(v) => patch({ icon: v })} testid="cms-price-method-pe-icon" />
                              </div>
                            )}
                          />
                          <p className="text-slate-300 font-semibold mb-2 mt-4">Métodos de pago — Internacional</p>
                          <FieldRow label="Etiqueta grupo internacional" value={l.pricing?.payment_methods_intl_label} onChange={(v) => setLandingSection("pricing", { payment_methods_intl_label: v })} testid="cms-price-intl-label" />
                          <ArrayEditor
                            items={l.pricing?.payment_methods_intl || []}
                            onChange={(next) => setLandingSection("pricing", { payment_methods_intl: next })}
                            blankItem={{ icon: "Card", label: "Nuevo método" }}
                            testidPrefix="cms-price-method-intl"
                            itemLabel="Método"
                            renderItem={(item, patch) => (
                              <div className="grid grid-cols-2 gap-3">
                                <FieldRow label="Etiqueta" value={item.label} onChange={(v) => patch({ label: v })} testid="cms-price-method-intl-label" />
                                <IconPicker value={item.icon} onChange={(v) => patch({ icon: v })} testid="cms-price-method-intl-icon" />
                              </div>
                            )}
                          />
                          <FieldRow label="Leyenda honesta Mercado Pago (PEN)" value={l.pricing?.mp_pen_note} onChange={(v) => setLandingSection("pricing", { mp_pen_note: v })} testid="cms-price-mp-pen-note" multiline />
                        </div>

                        <Button onClick={() => saveSection({ landing_json: l }, "sección Precio")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-pricing-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                        </Button>
                      </TabsContent>

                      {/* FAQ */}
                      <TabsContent value="faq" className="space-y-3">
                        <FieldRow label="Título FAQ" value={l.faq?.title} onChange={(v) => setLandingSection("faq", { title: v })} testid="cms-faq-title" />
                        <ArrayEditor
                          items={l.faq?.items || []}
                          onChange={(next) => setLandingSection("faq", { items: next })}
                          blankItem={{ q: "Nueva pregunta", a: "Respuesta..." }}
                          testidPrefix="cms-faq"
                          itemLabel="Pregunta"
                          renderItem={(item, patch) => (
                            <div className="space-y-2">
                              <FieldRow label="Pregunta" value={item.q} onChange={(v) => patch({ q: v })} testid="cms-faq-q" />
                              <FieldRow label="Respuesta" value={item.a} onChange={(v) => patch({ a: v })} testid="cms-faq-a" multiline />
                              <div className="grid grid-cols-2 gap-3">
                                <FieldRow label="Enlace URL (opcional)" value={item.link_to} onChange={(v) => patch({ link_to: v })} testid="cms-faq-linkto" />
                                <FieldRow label="Etiqueta enlace (opcional)" value={item.link_label} onChange={(v) => patch({ link_label: v })} testid="cms-faq-linklabel" />
                              </div>
                            </div>
                          )}
                        />
                        <Button onClick={() => saveSection({ landing_json: l }, "FAQ")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-faq-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar FAQ
                        </Button>
                      </TabsContent>

                      {/* CTA FINAL */}
                      <TabsContent value="cta" className="space-y-3">
                        <FieldRow label="Título" value={l.cta_final?.title} onChange={(v) => setLandingSection("cta_final", { title: v })} testid="cms-cta-title" />
                        <FieldRow label="Subtítulo" value={l.cta_final?.subtitle} onChange={(v) => setLandingSection("cta_final", { subtitle: v })} testid="cms-cta-subtitle" multiline />
                        <FieldRow label="Botón" value={l.cta_final?.button_label} onChange={(v) => setLandingSection("cta_final", { button_label: v })} testid="cms-cta-btn" />
                        <FieldRow label="Nota" value={l.cta_final?.note} onChange={(v) => setLandingSection("cta_final", { note: v })} testid="cms-cta-note" />
                        <Button onClick={() => saveSection({ landing_json: l }, "CTA final")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-cta-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                        </Button>
                      </TabsContent>

                      {/* FOOTER */}
                      <TabsContent value="footer" className="space-y-3">
                        <FieldRow label="Descripción de la marca" value={l.footer?.brand_description} onChange={(v) => setLandingSection("footer", { brand_description: v })} testid="cms-footer-desc" multiline />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <FieldRow label="Título 'Enlaces Rápidos'" value={l.footer?.quick_links_title} onChange={(v) => setLandingSection("footer", { quick_links_title: v })} testid="cms-footer-ql" />
                          <FieldRow label="Título 'Legal'" value={l.footer?.legal_title} onChange={(v) => setLandingSection("footer", { legal_title: v })} testid="cms-footer-legal" />
                          <FieldRow label="Título 'Contacto'" value={l.footer?.contact_title} onChange={(v) => setLandingSection("footer", { contact_title: v })} testid="cms-footer-ct" />
                        </div>
                        <FieldRow label="WhatsApp mostrado" value={l.footer?.whatsapp_display} onChange={(v) => setLandingSection("footer", { whatsapp_display: v })} testid="cms-footer-wa" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <FieldRow label="Made in" value={l.footer?.made_in} onChange={(v) => setLandingSection("footer", { made_in: v })} testid="cms-footer-madein" />
                          <FieldRow label="Empresa" value={l.footer?.company_line} onChange={(v) => setLandingSection("footer", { company_line: v })} testid="cms-footer-company" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <FieldRow label="Precio label" value={l.footer?.membership_price_label} onChange={(v) => setLandingSection("footer", { membership_price_label: v })} testid="cms-footer-mp-label" />
                          <FieldRow label="Precio" value={l.footer?.membership_price} onChange={(v) => setLandingSection("footer", { membership_price: v })} testid="cms-footer-mp" />
                          <FieldRow label="Período" value={l.footer?.membership_period} onChange={(v) => setLandingSection("footer", { membership_period: v })} testid="cms-footer-period" />
                        </div>
                        <Button onClick={() => saveSection({ landing_json: l }, "footer")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-footer-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                        </Button>
                      </TabsContent>

                      {/* USOS */}
                      <TabsContent value="usos" className="space-y-3">
                        <FieldRow label="Título" value={l.usos?.hero_title} onChange={(v) => setLandingSection("usos", { hero_title: v })} testid="cms-usos-title" />
                        <FieldRow label="Subtítulo" value={l.usos?.hero_subtitle} onChange={(v) => setLandingSection("usos", { hero_subtitle: v })} testid="cms-usos-sub" multiline />
                        <ArrayEditor
                          items={l.usos?.categories || []}
                          onChange={(next) => setLandingSection("usos", { categories: next })}
                          blankItem={{ key: "nuevo", title: "Nueva categoría", text: "Descripción..." }}
                          testidPrefix="cms-usos"
                          itemLabel="Categoría"
                          renderItem={(item, patch) => (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <FieldRow label="Título" value={item.title} onChange={(v) => patch({ title: v })} testid="cms-usos-cat-title" />
                              <FieldRow label="Descripción" value={item.text} onChange={(v) => patch({ text: v })} testid="cms-usos-cat-text" multiline />
                            </div>
                          )}
                        />
                        <Button onClick={() => saveSection({ landing_json: l }, "usos")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-usos-save-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                        </Button>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ============ PÁGINA NOSOTROS (estructurada) ============ */}
              <TabsContent value="about">
                <Card className="bg-[#14375F] border-white/15">
                  <CardContent className="p-4 md:p-6 space-y-3">
                    <p className="text-slate-400 text-sm">Esta pestaña edita la página <strong className="text-slate-200">/nosotros</strong> del menú. El documento markdown del footer se edita en Docs legales → "Nosotros (doc legal)".</p>
                    <FieldRow label="Título hero" value={content.about_json?.hero_title} onChange={(v) => setContent((c) => ({ ...c, about_json: { ...c.about_json, hero_title: v } }))} testid="cms-about-hero-title" />
                    <FieldRow label="Subtítulo hero (misión en una línea)" value={content.about_json?.hero_subtitle} onChange={(v) => setContent((c) => ({ ...c, about_json: { ...c.about_json, hero_subtitle: v } }))} testid="cms-about-hero-sub" multiline />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FieldRow label="Título historia" value={content.about_json?.history_title} onChange={(v) => setContent((c) => ({ ...c, about_json: { ...c.about_json, history_title: v } }))} testid="cms-about-history-title" />
                    </div>
                    <FieldRow label="Texto historia" value={content.about_json?.history_text} onChange={(v) => setContent((c) => ({ ...c, about_json: { ...c.about_json, history_text: v } }))} testid="cms-about-history-text" multiline />
                    <div className="pt-3 border-t border-white/15">
                      <p className="text-slate-300 font-semibold mb-2">Tarjetas de valores (3)</p>
                      <ArrayEditor
                        items={content.about_json?.values || []}
                        onChange={(next) => setContent((c) => ({ ...c, about_json: { ...c.about_json, values: next } }))}
                        blankItem={{ icon: "Shield", title: "Nuevo valor", text: "Descripción..." }}
                        testidPrefix="cms-about-value"
                        itemLabel="Valor"
                        renderItem={(item, patch) => (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FieldRow label="Título" value={item.title} onChange={(v) => patch({ title: v })} testid="cms-about-value-title" />
                            <IconPicker value={item.icon} onChange={(v) => patch({ icon: v })} testid="cms-about-value-icon" />
                            <div className="md:col-span-2">
                              <FieldRow label="Texto" value={item.text} onChange={(v) => patch({ text: v })} testid="cms-about-value-text" multiline />
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <Button onClick={() => saveSection({ about_json: content.about_json }, "página Nosotros")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-about-save-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar Página Nosotros
                    </Button>
                    <div className="pt-2">
                      <a href="/nosotros" target="_blank" rel="noopener noreferrer" className="text-[#5BC0FF] text-sm hover:underline inline-flex items-center gap-1"><Eye className="w-4 h-4" /> Vista previa</a>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ============ CONTACT PAGE ============ */}
              <TabsContent value="contact">
                <Card className="bg-[#14375F] border-white/15">
                  <CardContent className="p-4 md:p-6 space-y-3">
                    <FieldRow label="Título hero" value={content.contact_json?.hero_title} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, hero_title: v } }))} testid="cms-contact-hero-title" />
                    <FieldRow label="Subtítulo hero" value={content.contact_json?.hero_subtitle} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, hero_subtitle: v } }))} testid="cms-contact-hero-sub" multiline />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FieldRow label="WhatsApp mostrado" value={content.contact_json?.whatsapp_display} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, whatsapp_display: v } }))} testid="cms-contact-wa-display" />
                      <FieldRow label="Hint WhatsApp" value={content.contact_json?.whatsapp_hint} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, whatsapp_hint: v } }))} testid="cms-contact-wa-hint" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FieldRow label="Correo" value={content.contact_json?.email_display} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, email_display: v } }))} testid="cms-contact-email" hint="Se muestra en amarillo si contiene [COMPLETAR POR ADMIN]" />
                      <FieldRow label="Ubicación" value={content.contact_json?.location_display} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, location_display: v } }))} testid="cms-contact-location" />
                    </div>
                    <FieldRow label="Horario" value={content.contact_json?.office_hours_display} onChange={(v) => setContent((c) => ({ ...c, contact_json: { ...c.contact_json, office_hours_display: v } }))} testid="cms-contact-hours" />
                    <Button onClick={() => saveSection({ contact_json: content.contact_json }, "página Contacto")} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="cms-contact-save-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar Contacto
                    </Button>
                    <div className="pt-2">
                      <a href="/contacto" target="_blank" rel="noopener noreferrer" className="text-[#5BC0FF] text-sm hover:underline inline-flex items-center gap-1"><Eye className="w-4 h-4" /> Vista previa</a>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ============ LEGAL DOCS ============ */}
              <TabsContent value="legal">
                <Card className="bg-[#14375F] border-white/15">
                  <CardContent className="p-6">
                    <Tabs defaultValue="terms">
                      <TabsList className="bg-slate-700 mb-6 flex-wrap h-auto">
                        {legalTabs.map((t) => (
                          <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:bg-[#1B84FF] data-[state=active]:text-white" data-testid={`cms-legal-tab-${t.id}`}>
                            <t.icon className="w-4 h-4 mr-2" /> {t.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {legalTabs.map((tab) => (
                        <TabsContent key={tab.id} value={tab.id} className="space-y-4">
                          <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2"><tab.icon className="w-5 h-5 text-[#5BC0FF]" /> {tab.fullLabel}</h2>
                            <div className="flex gap-2">
                              <a href={tab.previewUrl} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="border-slate-600 text-slate-300"><Eye className="w-4 h-4 mr-2" /> Vista Previa</Button>
                              </a>
                              <Button onClick={() => saveSection({ [tab.field]: content[tab.field] }, tab.label)} disabled={saving} size="sm" className="bg-[#1B84FF] hover:bg-[#0F6FDB]">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                              </Button>
                            </div>
                          </div>
                          <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                            <p className="text-slate-400 text-sm">💡 Markdown: **negrita**, *cursiva*, # títulos, - listas</p>
                          </div>
                          <Textarea
                            value={content[tab.field]}
                            onChange={(e) => setContent({ ...content, [tab.field]: e.target.value })}
                            className="bg-slate-700 border-slate-600 text-white min-h-[500px] font-mono text-sm"
                            data-testid={`cms-legal-textarea-${tab.id}`}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminSiteContent;
